import { Input as AntdInput } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { FormItemInputContext } from 'antd/es/form/context';
import classNames from 'classnames';
import React, { ChangeEvent, FC, memo, ReactNode, useContext } from 'react';
import { Popup } from 'shared/ui/Popup';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';

import s from './index.module.scss';

type InputSize = 'extraSmall' | SizeType;

interface InputProps {
  value?: Nullable<string>;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  prefix?: ReactNode;
  className?: string;
  bordered?: boolean;
  size?: InputSize;
  disabled?: boolean;
  maxLength?: number;
  requiredMark?: boolean;
  tooltip?: string;
  label?: string;
  containerClassName?: string;
  onTouchStart?: (value: CustomAny) => void;
  readOnly?: boolean;
  uppercase?: boolean;
}

export const Input: FC<InputProps> = memo((props) => {
  const {
    value,
    placeholder,
    prefix,
    className,
    onChange,
    disabled,
    maxLength,
    bordered = false,
    size = 'middle',
    requiredMark,
    tooltip,
    label,
    containerClassName,
    onTouchStart,
    readOnly,
    uppercase,
  } = props;

  const { status } = useContext(FormItemInputContext);
  const isError = status === 'error';

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const inputValue = uppercase ? e.target.value.toUpperCase() : e.target.value;

    onChange?.(inputValue, e);
  };

  const handleTouch = (): void => {
    onTouchStart?.(value || []);
  };

  return (
    <div className={classNames('flex flex-col', containerClassName)}>
      <div className="flex">
        {label && <div className="mb-2.5 mr-2">{label}</div>}
        {tooltip && (
          <Popup title={label} description={tooltip}>
            <InfoIcon width={22} height={22} className="[&>path]:stroke-primaryLight" />
          </Popup>
        )}
      </div>
      {requiredMark && <div className="text-error text-2xl absolute z-10 top-0 left-1 pointer-events-none">*</div>}
      <AntdInput
        className={classNames(s.input, s[size], className)}
        status={isError ? 'error' : undefined}
        variant={bordered ? 'outlined' : 'borderless'}
        placeholder={placeholder}
        prefix={prefix}
        value={value || undefined}
        onChange={handleChange}
        disabled={disabled}
        maxLength={maxLength}
        onClick={handleTouch}
        readOnly={readOnly}
      />
    </div>
  );
});
